// warehouse/identity.js — purga por identidad de la bodega local (F6.4a).
//
// POR QUÉ unas fronteras purgan y otras no (léelo antes de tocar la tabla):
//
// La bodega guarda el espejo COMPLETO del material de estudio de UN usuario
// (un solo dueño a la vez, sin user_id en las tablas — schema.js). En un
// dispositivo compartido, dejar filas del dueño anterior al entrar una
// identidad nueva es el mismo problema de privacidad que los "docs fantasma"
// del screenDataCache (TestFlight 0.9.0) pero PERSISTIDO en disco. Por eso el
// aislamiento se logra BORRANDO físicamente en la frontera, no filtrando.
//
// Pero el borrón NO es uniforme como el de beginSessionTransition, porque la
// bodega es cara de reconstruir (descargas) y a veces la identidad NO cambia:
//   - Sesión expirada de un usuario REGISTRADO: el dueño probablemente va a
//     volver a loguearse como él mismo. Purgar aquí tiraría una bodega válida;
//     se conserva y la decisión real se toma en el próximo login (comparando
//     owner_user_id). Si entra otro, ahí sí se purga.
//   - Sesión expirada de un GUEST: la cuenta guest ya no existe en el server —
//     no hay a quién conservarle nada. Purga total.
//   - Login / guest nuevo / verificación / OAuth: si el que entra ES el dueño
//     registrado en sync_meta, se conserva (mismo usuario, mismo material).
//     Si es otro (o no había dueño), purga total y se registra el dueño nuevo.
//   - Logout: petición explícita de cerrar TODO en este dispositivo (incluye
//     borrado de cuenta, que desemboca aquí). Purga total sin comparar.
//   - Conversión guest→cuenta: la ÚNICA transición donde la identidad cambia
//     de id pero la persona y su material son los mismos (mismo dispositivo,
//     continuidad explícita). Se conserva todo y solo se sobrescribe el dueño.
//
// "Purga TODO" = purgeWarehouseStorage (connection.js): filas de todas las
// tablas en UNA transacción + archivo .db + AMBAS copias de la llave de
// cifrado destruidas JUNTAS (nunca una sin la otra).
//
// Esta función es HERMANA de beginSessionTransition (AuthContext.js), llamada
// en los mismos call sites; no lo reemplaza ni cambia su comportamiento.

import {
  isNativePlatform,
  openWarehouse,
  purgeWarehouseStorage,
} from './connection';
import { SYNC_META_KEYS } from './schema';

// Las fronteras de identidad (tabla F6.4a §4). El call site elige la variante
// de sesión expirada según si el usuario que expiró era guest.
export const WAREHOUSE_BOUNDARY = {
  SESSION_EXPIRED: 'session-expired',
  SESSION_EXPIRED_GUEST: 'session-expired-guest',
  LOGIN: 'login',
  GUEST_CREATED: 'guest-created',
  EMAIL_VERIFIED: 'email-verified',
  LOGOUT: 'logout',
  // ajustes-1-3-0 (Bloque H2, #507): el cierre de sesion se parte en tres. Un REGISTRADO
  // conserva su bodega (LOGOUT); un invitado la pierde (LOGOUT_GUEST: la cuenta muere con
  // la sesion); una cuenta BORRADA en el servidor la pierde (ACCOUNT_DELETED).
  LOGOUT_GUEST: 'logout-guest',
  ACCOUNT_DELETED: 'account-deleted',
  OAUTH_CALLBACK: 'oauth-callback',
  GUEST_CONVERTED: 'guest-converted',
};

// Acciones posibles de una frontera.
const KEEP = 'keep';                                   // no tocar nada
const PURGE_ALWAYS = 'purge-always';                   // purga total sin comparar
const PURGE_IF_DIFFERENT_OWNER = 'purge-if-different'; // compara owner_user_id
const ADOPT_OWNER = 'adopt-owner';                     // conserva; solo cambia el dueño
// ajustes-1-3-0 (Bloque H2, #507): conserva el material y el dueno (para que LOGIN compare) y
// olvida SOLO la foto del usuario, que es lo que arma el arranque sin red.
const KEEP_FORGET_USER = 'keep-forget-user';

// El mapa frontera → acción, calcado de la tabla de la sección 4. Una
// frontera nueva se agrega AQUÍ (y en WAREHOUSE_BOUNDARY), no como un if
// suelto en otro lado.
const BOUNDARY_ACTIONS = {
  [WAREHOUSE_BOUNDARY.SESSION_EXPIRED]: KEEP,
  [WAREHOUSE_BOUNDARY.SESSION_EXPIRED_GUEST]: PURGE_ALWAYS,
  [WAREHOUSE_BOUNDARY.LOGIN]: PURGE_IF_DIFFERENT_OWNER,
  [WAREHOUSE_BOUNDARY.GUEST_CREATED]: PURGE_IF_DIFFERENT_OWNER,
  [WAREHOUSE_BOUNDARY.EMAIL_VERIFIED]: PURGE_IF_DIFFERENT_OWNER,
  // ajustes-1-3-0 (Bloque H2, #507; Carlos: "si de acuerdo"): cerrar sesion CONSERVA la bodega
  // de esa cuenta. Al volver a entrar, LOGIN compara el dueno: la misma cuenta la reutiliza
  // (las tarjetas salen Listo de inmediato, cero descargas) y otra cuenta la purga ANTES del
  // primer pintado (login() espera esta frontera antes de setUser). Decia PURGE_ALWAYS, y
  // esa purga era la que volvia a pintar "procesando" dos documentos ya bajados en cada
  // cierre de sesion + entrada. La purga sigue para el invitado y la cuenta borrada.
  [WAREHOUSE_BOUNDARY.LOGOUT]: KEEP_FORGET_USER,
  [WAREHOUSE_BOUNDARY.LOGOUT_GUEST]: PURGE_ALWAYS,
  [WAREHOUSE_BOUNDARY.ACCOUNT_DELETED]: PURGE_ALWAYS,
  [WAREHOUSE_BOUNDARY.OAUTH_CALLBACK]: PURGE_IF_DIFFERENT_OWNER,
  [WAREHOUSE_BOUNDARY.GUEST_CONVERTED]: ADOPT_OWNER,
};

async function readOwnerUserId(api) {
  const rows = await api.query(
    'SELECT value FROM sync_meta WHERE key = ?;',
    [SYNC_META_KEYS.OWNER_USER_ID]
  );
  return rows.length > 0 && rows[0].value ? rows[0].value : null;
}

async function writeOwnerUserId(api, userId) {
  await api.run(
    'INSERT OR REPLACE INTO sync_meta (key, value) VALUES (?, ?);',
    [SYNC_META_KEYS.OWNER_USER_ID, userId],
    false
  );
}

// Serializa las operaciones de frontera: dos transiciones pegadas (p. ej.
// logout inmediato tras un login) no deben entrelazar sus purgas. Mismo
// espíritu que el dedupe de inflight en screenDataCache, pero en cadena.
let boundaryQueue = Promise.resolve();

async function applyBoundary(boundary, newUserId) {
  // F6.7a: cualquier frontera de identidad cancela los reintentos con backoff
  // del downloader. Un timer programado por el dueño anterior no debe despertar
  // dentro de la sesión del siguiente — y aunque la época ya impediría que
  // ESCRIBIERA algo, dejarlo vivo gastaría una descarga completa a nombre de
  // nadie. Import dinámico: identity.js no arrastra el chunk del downloader al
  // bundle principal (el patrón tokenStore de todo el módulo).
  try {
    const dl = await import('./downloader');
    dl.resetDownloaderRetries();
    // TANDA 32: y el respiro del freno del ciclo caliente, por el MISMO motivo y
    // con el mismo alcance. Va en su propia función porque `resetDownloaderRetries`
    // la llaman además los disparadores externos, y el freno no puede desarmarse
    // con lo mismo que tiene que frenar (ver su docstring).
    dl.resetDownloaderHotCycle?.();
  } catch (e) { /* sin downloader cargado no hay timers que cancelar */ }

  // Fail-closed: una frontera desconocida es un bug de programación, pero en
  // producción el default seguro para la privacidad es purgar, no conservar.
  const action = BOUNDARY_ACTIONS[boundary] ?? PURGE_ALWAYS;

  if (action === KEEP) {
    // Ni siquiera se abre la bodega: la frontera "sesión expirada (registrado)"
    // se resuelve en el próximo login, que sí compara dueños.
    return;
  }

  if (action === PURGE_ALWAYS) {
    await purgeWarehouseStorage();
    return;
  }

  if (action === ADOPT_OWNER) {
    // Conversión guest→cuenta: continuidad explícita de identidad. Nada se
    // borra; el dueño pasa a ser el id nuevo.
    const api = await openWarehouse();
    if (newUserId) await writeOwnerUserId(api, newUserId);
    return;
  }
  if (action === KEEP_FORGET_USER) {
    const api = await openWarehouse();
    await api.run('DELETE FROM sync_meta WHERE key = ?;', [SYNC_META_KEYS.USER_SNAPSHOT], false);
    return;
  }

  // PURGE_IF_DIFFERENT_OWNER
  const api = await openWarehouse();
  const owner = await readOwnerUserId(api);
  if (owner !== null && owner === newUserId) {
    // El que entra ES el dueño registrado — se conserva todo.
    return;
  }
  // Dueño distinto, o no había dueño registrado → purga total y se deja la
  // bodega vacía con el dueño nuevo anotado, lista para la próxima descarga.
  await purgeWarehouseStorage();
  if (newUserId) {
    const fresh = await openWarehouse();
    await writeOwnerUserId(fresh, newUserId);
  }
}

/**
 * Frontera de identidad de la bodega. Llamar JUNTO a beginSessionTransition
 * en cada call site de AuthContext, con la frontera que corresponde.
 *
 * Nunca lanza y nunca bloquea un flujo de auth más allá de los timeouts del
 * plugin (connection.js): la bodega es un espejo re-descargable — un fallo
 * aquí degrada a "sin bodega", jamás a "sin login".
 */
export function syncWarehouseIdentity(boundary, newUserId = null) {
  if (!isNativePlatform()) return Promise.resolve();
  const run = boundaryQueue.then(() => applyBoundary(boundary, newUserId));
  // La cadena nunca queda rota: el siguiente en la fila arranca aunque este
  // fallara; el error se contiene aquí (best-effort, patrón tokenStore).
  boundaryQueue = run.catch(() => {});
  return run.catch(() => {});
}

// ── Epoch de la bodega (para F6.4b) ──────────────────────────────────────────
// Contador warehouse_epoch en sync_meta: toda escritura larga futura (las
// descargas de F6.4b) captura el epoch al empezar y compara antes de cada
// COMMIT; si cambió (o ya no hay bodega), aborta sin escribir — mismo patrón
// sin drenaje que screenDataCache.generation y sessionVersion. La purga
// invalida el epoch por construcción: borra el archivo, y la reapertura
// siembra un epoch nuevo con Date.now() (connection.js), que nunca repite un
// valor capturado antes.

/** Lee el epoch vigente; null si no hay bodega legible en este momento. */
export function getWarehouseEpoch() {
  if (!isNativePlatform()) return Promise.resolve(null);
  return (async () => {
    try {
      const api = await openWarehouse();
      const rows = await api.query(
        'SELECT value FROM sync_meta WHERE key = ?;',
        [SYNC_META_KEYS.WAREHOUSE_EPOCH]
      );
      return rows.length > 0 ? Number(rows[0].value) : null;
    } catch (e) {
      return null;
    }
  })();
}

/** Invalida manualmente el epoch (F6.4b lo usará al re-descargar un pool). */
export function bumpWarehouseEpoch() {
  if (!isNativePlatform()) return Promise.resolve(null);
  return (async () => {
    try {
      const api = await openWarehouse();
      await api.run(
        'UPDATE sync_meta SET value = CAST(value AS INTEGER) + 1 WHERE key = ?;',
        [SYNC_META_KEYS.WAREHOUSE_EPOCH],
        false
      );
      return getWarehouseEpoch();
    } catch (e) {
      return null;
    }
  })();
}
